import { useEffect, useRef, useState } from 'react';
import Cropper from 'cropperjs';
import { MODAL_AVATAR_ACTIONS } from '../stores/reducers/modal';


interface ContainerConfig {
  width: number;
  height: number;
  padding: number;
}

interface CropBoxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface UseCropperProps {
  isActive: boolean;
  modalAvatarAction: string;
  originalImage: string;
  onReady?: () => void;
  container?: ContainerConfig;
  avatarBox?: CropBoxRect;
}

const DEFAULT_CONTAINER: ContainerConfig = {
  width: 560,
  height: 316,
  padding: 32,
};

export default function useCropper({
  isActive,
  modalAvatarAction,
  originalImage,
  onReady,
  container = DEFAULT_CONTAINER,
  avatarBox,
}: UseCropperProps) {
  const imageElementRef = useRef<HTMLImageElement>(null);
  const cropperRef = useRef<Cropper | null>(null);
  
  const [zoomRatio, setZoomRatio] = useState<number>(1);
  const [minSliderZoom, setMinSliderZoom] = useState<number>(0);
  const [maxSliderZoom, setMaxSliderZoom] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isAvatarMode = modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR;
  const aspectRatio = isAvatarMode ? 1 : (16 / 9);

  const calculateCropBox = (containerCfg: ContainerConfig, override?: CropBoxRect): CropBoxRect => {
    if (override) return override;

    const { width: contW, height: contH, padding } = containerCfg;

    const availW = contW - padding * 2;
    const availH = contH - padding * 2;

    // Compute crop size to fit within available area with target aspect ratio
    let cropW = availW;
    let cropH = cropW / aspectRatio;
    if (cropH > availH) {
      cropH = availH;
      cropW = cropH * aspectRatio;
    }

    const left = padding + (availW - cropW) / 2;
    const top = padding + (availH - cropH) / 2;

    return { left, top, width: cropW, height: cropH };
  };

  const setupCropBoxHelper = () => {
    const cropperTop = document.getElementsByClassName('cropper-point point-n');
    const helperDiv = document.getElementById('crop-box-helper');
    if (cropperTop[0] && helperDiv) {
      cropperTop[0].appendChild(helperDiv);
    }
  };

  const calculateZoomLimits = (imageData: any, cropBox: CropBoxRect) => {
    const minZoom = Math.max(
      cropBox.width / imageData.naturalWidth,
      cropBox.height / imageData.naturalHeight
    );
    const maxZoom = minZoom * 3;
    return { minZoom, maxZoom };
  };

  useEffect(() => {
    if (!imageElementRef.current || !isActive || !originalImage) {
      return;
    }

    setIsLoading(true);

    cropperRef.current = new Cropper(imageElementRef.current, {
      aspectRatio,
      guides: false,
      center: false,
      responsive: false,
      rotatable: false,
      zoomOnTouch: false,
      highlight: false,
      cropBoxResizable: false,
      cropBoxMovable: false,
      viewMode: 0,
      dragMode: 'move',
      autoCropArea: 0,
      ready: () => {
        if (!cropperRef.current) return;

        setupCropBoxHelper();

        const imageData = cropperRef.current.getImageData();
        const finalContainer: ContainerConfig = container ?? DEFAULT_CONTAINER;
        const cropBox = calculateCropBox(finalContainer, avatarBox);
        const { minZoom, maxZoom } = calculateZoomLimits(imageData, cropBox);

        cropperRef.current.setCropBoxData(cropBox);
        setMinSliderZoom(minZoom);
        setMaxSliderZoom(maxZoom);
        setZoomRatio(minZoom);

        cropperRef.current.zoomTo(minZoom);

        setIsLoading(false);
        onReady?.();
      },
      zoom: (event: CustomEvent) => {
        if (!cropperRef.current) return;

        if (event.detail.ratio > 10) {
          event.preventDefault();
          cropperRef.current.zoomTo(10);
          setZoomRatio(10);
        } else {
          setZoomRatio(event.detail.ratio);
        }
      },
    });

    return () => {
      if (cropperRef.current) {
        cropperRef.current.destroy();
        cropperRef.current = null;
      }
    };
  }, [
    isActive,
    originalImage,
    aspectRatio,
    onReady,
    container?.width,
    container?.height,
    container?.padding,
    avatarBox?.left,
    avatarBox?.top,
    avatarBox?.width,
    avatarBox?.height,
  ]);

  const zoomTo = (ratio: number) => {
    if (cropperRef.current) {
      cropperRef.current.zoomTo(ratio);
    }
  };

  const getCroppedCanvas = () => {
    return cropperRef.current?.getCroppedCanvas() || null;
  };

  const zoomWithSlider = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.valueAsNumber) {
      zoomTo(event.target.valueAsNumber);
    }
  };

  return {
    imageElementRef,
    cropperRef,
    zoomRatio,
    minSliderZoom,
    maxSliderZoom,
    isLoading,
    zoomTo,
    getCroppedCanvas,
    zoomWithSlider,
  };
}
